'use client'
import {signOut, useSession} from 'next-auth/react'
import {useRouter,usePathname} from 'next/navigation'
import {createContext, ReactNode, useEffect, useState} from 'react'
import {Account, User, UserState} from '@stock.front/prisma'

import {updateUserQuery} from './queries/updateUserQuery'
import {useDebouncedCallback} from 'use-debounce'
import Debugger from "@/utils/debugger";
import {trpcClient} from "@/lib/trpc-client";

export const userContext = createContext<{
    user?: User
    accountVirtual?: Account
    accountCredit?: Account
    accountEarnings?:Account
    isLoading: boolean
    logOut: () => void
    refetchAccounts: () => void
    updateUser: (newUser: Partial<User>) => void
}>({
    isLoading: false,
    logOut: () => {
        Debugger.log('logOut not implemented')
    },
    updateUser: () => {
        Debugger.log('updateUser not implemented')
    },
    refetchAccounts: ()=> {
        Debugger.log('refetchAccounts not implemented')
    }
})

const debounceTimeout = 1000
const signPages = ['/portfolio','/portfolio-simulation','/recommendation','/account','/member','/trading-record','/security-center','/profile','/customer','/account-profit','/account-credit','/account-simulation','/td-extension','/td-extension-simulation']

type UserProviderProps = {
    children: ReactNode
    workspaceId: string
}

export const UserProvider = ({children, workspaceId}: UserProviderProps) => {
    const router = useRouter();
    const pathName = usePathname();
    const {data: session, status} = useSession()
    const [user, setUser] = useState<User | undefined>()

    const {data: accounts, refetch:refetchAccounts} = trpcClient.user.userAccounts.useQuery(
        {}, {enabled: !!user}
    )

    useEffect(() => {
        const parsedUser = session?.user as User
        setUser(parsedUser)
    }, [session])

    useEffect(() => {
        if (status === 'loading') return
        const isSigningIn = () => pathName && signPages.includes(pathName.replace(``,''));
        if (!user && status === 'unauthenticated' && isSigningIn()) {
            router.replace(`/login?redirect=${pathName}`)
        }
        if (user && user.state == UserState.INVALID && pathName !='/new-password') {
            router.push(`/new-password`)
        }
    }, [router, status, user, pathName])

    const updateUser = async (updates: Partial<User>) => {
        if(user) {
            const newUser = { ...user, ...updates }
            await saveUser(newUser)
            setUser(newUser)
        }
    }

    const saveUser = useDebouncedCallback(
        async (newUser?: Partial<User>) => {
            // if (isNotDefined(user)) return
            if(user) {
                await updateUserQuery(user.id, {...user, ...newUser})
                await refreshUser()
            }
        }, debounceTimeout
    )

    const logOut = () => {
        signOut()
        setUser(undefined)
    }

    useEffect(() => {
        return () => {
            saveUser.flush()
        }
    }, [saveUser])

    return (
        <userContext.Provider
            value={{
                user,
                accountEarnings: accounts?.accountEarnings,
                accountCredit: accounts?.accountCredit,
                accountVirtual: accounts?.accountVirtual,
                isLoading: status === 'loading',
                logOut,
                refetchAccounts,
                updateUser,
            }}
        >
            {children}
        </userContext.Provider>
    )
}

export const refreshUser = async () => {
    await fetch('/api/auth/session?update')
    reloadSession()
}

const reloadSession = () => {
    const event = new Event('visibilitychange')
    document.dispatchEvent(event)
}
